import React, { createContext, useState } from "react";
import { ReactNode } from "react";
import {
  Configuration,
  IngressApi,
  ContextApi,
  FetchParams,
  RequestContext,
  ContentApi,
  GuidApi,
  IngressResponse,
  StringIngressRequest,
  StringObjectIDictionaryIngressRequest,
  StringObjectIDictionaryIngressRequestStageContent,
} from "tsclient/V1";
import useAuth from "src/hooks/useAuth";
import { CsvData, DataTable } from "src/types/dataTable";

// Types

export interface HistoryEntry
  extends StringObjectIDictionaryIngressRequestStageContent {}

export interface ApiContextValue {
  ingressApi?: IngressApi;
  contextApi?: ContextApi;
  uploadCsv: (
    data: StringIngressRequest,
    validateOnly?: boolean
  ) => Promise<DataTable>;
  downloadFile: (contextId: string, requestId: string) => Promise<Blob>;
  downloadComplete: (contextId: string) => Promise<Blob>;
  uploadJson: (
    data: DataTable,
    meta: StringObjectIDictionaryIngressRequest
  ) => Promise<IngressResponse>;
  getHistory: (contextId: string, size: number) => Promise<HistoryEntry[]>;
}

interface ApiProviderProps {
  children?: ReactNode;
}

// ActionTypes

// Config

export const basePath = process.env.REACT_APP_API_ENDPOINT
  ? process.env.REACT_APP_API_ENDPOINT
  : `https://${process.env.REACT_APP_API_HOST}`;
const ApiConfig: Configuration = new Configuration({ basePath });
export const IngressAPIClient = new IngressApi(ApiConfig);
export const ContextAPIClient = new ContextApi(ApiConfig);
export const GuidAPIClient = new GuidApi(ApiConfig);
export const ContentAPIClient = new ContentApi(ApiConfig);
export const FALLBACK_ERROR_MESSAGE = "An error occured";

export const DEFAULT_ERROR_MAPPING: Record<number, string> = {
  400: "apiCodes.400",
  401: "NotAuthorized",
  404: "apiCodes.404",
  500: "apiCodes.500",
};

// Methods
export const tokenMiddleware = (accessToken?: string) => async (
  context: RequestContext
): Promise<FetchParams | void> => {
  const { url, init } = context;
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${accessToken}`);
  init.headers = headers;

  return { init, url };
};

const ApiContext = createContext<ApiContextValue>({
  uploadCsv: () => null,
  uploadJson: () => null,
  getHistory: () => null,
  downloadFile: () => null,
  downloadComplete: () => null,
});

export const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
  const [ingressApi, setIngressApi] = useState<IngressApi>();
  const [contextApi, setContextApi] = useState<ContextApi>();
  const [guidApi, setGuidApi] = useState<GuidApi>();
  const [contentApi, setContentApi] = useState<ContentApi>();
  const { activeAccount } = useAuth();

  // Init Api
  React.useEffect(() => {
    if (
      activeAccount !== null &&
      activeAccount.accessToken !== null &&
      activeAccount.accessToken !== ""
    ) {
      const ingressClient = IngressAPIClient.withPreMiddleware(
        tokenMiddleware(activeAccount.accessToken)
      );
      const contextClient = ContextAPIClient.withPreMiddleware(
        tokenMiddleware(activeAccount.accessToken)
      );
      const guidClient = GuidAPIClient.withPreMiddleware(
        tokenMiddleware(activeAccount.accessToken)
      );
      const contentClient = ContentAPIClient.withPreMiddleware(
        tokenMiddleware(activeAccount.accessToken)
      );

      setIngressApi(ingressClient);
      setContextApi(contextClient);
      setGuidApi(guidClient);
      setContentApi(contentClient);
    }
  }, [activeAccount]);

  // Actions

  const uploadCsv = async (data: CsvData, validateOnly?: boolean) => {
    if (ingressApi && guidApi) {
      let guid = await guidApi.guidGet();
      guid = guid.replace('"', "").replace('"', "");
      const author =
        activeAccount.account.name || activeAccount.account.username;

      const finalData = { ...data, id: guid, author, timestamp: new Date() };

      return (await ingressApi.ingressCsvPost({
        dryRun: validateOnly || false,
        stringIngressRequest: finalData,
      })) as Promise<DataTable>;
    } else {
      throw new Error();
    }
  };

  const uploadJson = async (
    data: DataTable,
    meta: StringObjectIDictionaryIngressRequest
  ) => {
    if (ingressApi) {
      const attributeNames = data.columnInformation.map((c) => c.name);
      let payload = data.rows.map((r) => {
        let obj = {};
        attributeNames.forEach((name, i) => {
          obj[name] = i < r.elements.length ? r.elements[i].value : undefined;
        });
        return obj;
      });
      const author =
        activeAccount.account.name || activeAccount.account.username;
      return await ingressApi.ingressJsonPost({
        stringObjectIDictionaryIngressRequest: {
          ...meta,
          timestamp: new Date(),
          author,
          payload,
        },
      });
    } else {
      throw new Error();
    }
  };

  const downloadFile = async (contextId: string, requestId: string) => {
    return await contentApi.contentContextIdOriginalRequestIdGet({
      contextId,
      requestId,
    });
  };

  const downloadComplete = async (contextId: string) => {
    return await contentApi.contentContextIdCompleteGet({
      contextId,
    });
  };

  const getHistory = async (
    contextId: string,
    size: number
  ): Promise<HistoryEntry[]> => {
    if (!contentApi) throw new Error();

    const list = contentApi.contentContextIdProcessLayerGet({
      processLayer: "post_validation",
      contextId,
      count: size,
    });

    return list;
  };

  return (
    <ApiContext.Provider
      value={{
        ingressApi,
        contextApi,
        uploadCsv,
        uploadJson,
        downloadFile,
        downloadComplete,
        getHistory,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export const ApiConsumer = ApiContext.Consumer;

export default ApiContext;
